import { CSSProperties } from 'react';
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import colors from '../../../../assets/theme/base/colors';
import { BadgeTone } from './IndividualJustifications/recruitmentPromotionMovementsHelper';

interface JustificationExpectationModalProps {
    open: boolean;
    onClose: () => void;
}

const headCell: CSSProperties = {
    padding: '8px 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textAlign: 'left',
    color: colors.ui.secondaryText,
    backgroundColor: colors.ui.subtotalRowBackground,
    border: `1px solid ${colors.ui.border}`,
};

const cell: CSSProperties = {
    padding: '10px 12px',
    fontSize: '0.8125rem',
    color: colors.dark.main,
    border: `1px solid ${colors.ui.border}`,
    verticalAlign: 'middle',
};

const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
} as const;

interface MatrixRow {
    priority: { label: string; tone: BadgeTone };
    deviation: { label: string; tone: BadgeTone };
    scenario: string;
    expectation: string;
}

const matrix: MatrixRow[] = [
    { priority: { label: 'P1 - highest priority', tone: 'positive' }, deviation: { label: 'No', tone: 'positive' }, scenario: 'Ideal', expectation: 'Justification not required' },
    { priority: { label: 'Not P1', tone: 'negative' }, deviation: { label: 'No', tone: 'positive' }, scenario: 'Acceptable', expectation: 'Justification recommended' },
    { priority: { label: 'P1 - highest priority', tone: 'positive' }, deviation: { label: 'Yes', tone: 'negative' }, scenario: 'Edge case', expectation: 'Justification required' },
    { priority: { label: 'Not P1', tone: 'negative' }, deviation: { label: 'Yes', tone: 'negative' }, scenario: 'Double deviation', expectation: 'Justification required' },
    { priority: { label: 'Any', tone: 'neutral' }, deviation: { label: 'N/A', tone: 'neutral' }, scenario: 'No EE Plan target exists', expectation: 'Justification not required' },
];

function Pill({ label, tone }: { label: string; tone: BadgeTone }) {
    const palette = colors.deviationBadge[tone];
    return <Box component="span" sx={{ ...pillSx, backgroundColor: palette.bg, color: palette.text }}>{label}</Box>;
}

export function JustificationExpectationModal({ open, onClose }: JustificationExpectationModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <CustomDialogTitle onClose={onClose}>Recruitment and Promotion Justifications</CustomDialogTitle>
            <DialogContent dividers>
                <MDTypography variant="body2" paragraph>
                    These two columns show whether each appointment addressed the highest priority demographic gap at that occupational level, and whether it was aligned to the EE Plan target. Use this to identify movements that require justification.
                </MDTypography>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={headCell}>Priority cell</th>
                            <th style={headCell}>Deviation badge</th>
                            <th style={headCell}>Scenario</th>
                            <th style={headCell}>Justification expectation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matrix.map(row => (
                            <tr key={`${row.scenario}`}>
                                <td style={cell}><Pill label={row.priority.label} tone={row.priority.tone} /></td>
                                <td style={cell}><Pill label={row.deviation.label} tone={row.deviation.tone} /></td>
                                <td style={cell}>{row.scenario}</td>
                                <td style={cell}>{row.expectation}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="contained" color="primary" size="small">Got it</MDButton>
            </DialogActions>
        </Dialog>
    );
}
